import { useEffect, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_SENTINELA_API_URL || 'https://api.antifraud.divipay.com.br';
const PUBLISHABLE_KEY = process.env.REACT_APP_SENTINELA_PUBLISHABLE_KEY || '';

/**
 * Teto de espera pelo `commit()` do SDK, em ms.
 *
 * O `commit()` carrega o FingerprintJS do CDN e faz `POST /v1/sessions` com um `fetch`
 * SEM AbortSignal. Se a API estiver de pé mas pendurada (LB segurando a conexão, deploy
 * travado), esse fetch fica pendente por minutos — e como o `commit()` acontece ANTES da
 * chamada de pagamento, o submit do cartão nunca sai: o pagador vê o spinner girando e
 * desiste, ou recarrega e queima tentativas na trava de 3 recusas.
 *
 * try/catch não resolve isso: ele cobre REJEIÇÃO, não ESPERA. A única forma sancionada de
 * a Sentinela impedir um pagamento é DENY com a flag de bloqueio ligada — no servidor.
 */
export const SENTINELA_COMMIT_TIMEOUT_MS = 800;

interface SentinelaInstance {
  commit: () => Promise<string>;
  destroy: () => void;
}

type SdkLoader = () => Promise<any>;

/**
 * Carrega o SDK da Sentinela e devolve um `commit()` para o submit do cartão.
 *
 * O `webpackIgnore` impede o CRA de tentar resolver a URL externa em build-time.
 * Nenhum caminho lança: SDK bloqueado por adblocker, CSP ou chave ausente devolve
 * `null` e o checkout segue sem sinais de comportamento.
 */
const defaultLoader: SdkLoader = () => import(/* webpackIgnore: true */ `${API_URL}/v1/sdk.js`);

export function useSentinela(
  publishableKey: string = PUBLISHABLE_KEY,
  loader: SdkLoader = defaultLoader,
) {
  const ref = useRef<SentinelaInstance | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!publishableKey) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const mod = await loader();
        if (cancelled) return;
        const instance = await mod.Antifraud.init({ publishableKey, apiBaseUrl: API_URL });
        if (cancelled) {
          instance.destroy();
          return;
        }
        ref.current = instance;
        setReady(true);
      } catch (err) {
        // SDK indisponível — o checkout segue sem fingerprinting.
        console.warn('Sentinela SDK indisponível', err);
      }
    })();

    return () => {
      cancelled = true;
      ref.current?.destroy();
      ref.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishableKey]);

  return {
    ready,
    commit: async (): Promise<string | null> => {
      if (!ref.current) return null;

      let timer: ReturnType<typeof setTimeout> | undefined;

      try {
        const bounded = new Promise<null>((resolve) => {
          timer = setTimeout(() => resolve(null), SENTINELA_COMMIT_TIMEOUT_MS);
        });

        return await Promise.race([
          ref.current.commit().catch((err) => {
            console.warn('Sentinela commit falhou', err);
            return null;
          }),
          bounded,
        ]);
      } catch (err) {
        console.warn('Sentinela commit falhou', err);
        return null;
      } finally {
        if (timer) clearTimeout(timer);
      }
    },
  };
}
